// Small, dependency-free UTM helpers used by the design registration workflow.
// UTM zone/datum metadata is deliberately explicit: coordinates are never
// shifted into a recording by guessing from its bounds.

const ELLIPSOIDS = {
    WGS84: { a: 6378137.0, inverseFlattening: 298.257223563 },
    GRS80: { a: 6378137.0, inverseFlattening: 298.257222101 },
    GDA94: { a: 6378137.0, inverseFlattening: 298.257222101 },
    GDA2020: { a: 6378137.0, inverseFlattening: 298.257222101 }
};

const K0 = 0.9996;
const DEG = Math.PI / 180;

export const validateUtmCrs = (crs) => {
    if (!crs || !Number.isInteger(Number(crs.zone)) || Number(crs.zone) < 1 || Number(crs.zone) > 60)
        throw new Error('UTM zone must be an integer from 1 to 60');
    if (crs.hemisphere !== 'N' && crs.hemisphere !== 'S')
        throw new Error('UTM hemisphere must be N or S');
    if (!Object.prototype.hasOwnProperty.call(ELLIPSOIDS, crs.datum))
        throw new Error('Select a supported UTM datum');
    return { zone: Number(crs.zone), hemisphere: crs.hemisphere, datum: crs.datum };
};

const ellipsoidFor = (crs) => {
    const normalized = validateUtmCrs(crs);
    const item = ELLIPSOIDS[normalized.datum];
    const f = 1 / item.inverseFlattening;
    return { ...normalized, a: item.a, f, e2: f * (2 - f), ep2: (f * (2 - f)) / (1 - f * (2 - f)) };
};

export const utmToGeographic = (easting, northing, crs) => {
    const c = ellipsoidFor(crs);
    if (!Number.isFinite(easting) || !Number.isFinite(northing)) throw new Error('UTM coordinates must be finite');

    const x = easting - 500000;
    const y = c.hemisphere === 'S' ? northing - 10000000 : northing;
    const m = y / K0;
    const mu = m / (c.a * (1 - c.e2 / 4 - 3 * c.e2 * c.e2 / 64 - 5 * c.e2 * c.e2 * c.e2 / 256));
    const e1 = (1 - Math.sqrt(1 - c.e2)) / (1 + Math.sqrt(1 - c.e2));
    const j1 = 3 * e1 / 2 - 27 * e1 ** 3 / 32;
    const j2 = 21 * e1 * e1 / 16 - 55 * e1 ** 4 / 32;
    const j3 = 151 * e1 ** 3 / 96;
    const j4 = 1097 * e1 ** 4 / 512;
    const fp = mu + j1 * Math.sin(2 * mu) + j2 * Math.sin(4 * mu) + j3 * Math.sin(6 * mu) + j4 * Math.sin(8 * mu);
    const sinFp = Math.sin(fp), cosFp = Math.cos(fp), tanFp = Math.tan(fp);
    const c1 = c.ep2 * cosFp * cosFp;
    const t1 = tanFp * tanFp;
    const n1 = c.a / Math.sqrt(1 - c.e2 * sinFp * sinFp);
    const r1 = c.a * (1 - c.e2) / ((1 - c.e2 * sinFp * sinFp) ** 1.5);
    const d = x / (n1 * K0);
    const latitude = fp - (n1 * tanFp / r1) * (d * d / 2 -
        (5 + 3 * t1 + 10 * c1 - 4 * c1 * c1 - 9 * c.ep2) * d ** 4 / 24 +
        (61 + 90 * t1 + 298 * c1 + 45 * t1 * t1 - 252 * c.ep2 - 3 * c1 * c1) * d ** 6 / 720);
    const longitude = ((Number(c.zone) - 1) * 6 - 180 + 3) * DEG +
        (d - (1 + 2 * t1 + c1) * d ** 3 / 6 +
         (5 - 2 * c1 + 28 * t1 - 3 * c1 * c1 + 8 * c.ep2 + 24 * t1 * t1) * d ** 5 / 120) / cosFp;
    return { latitude: latitude / DEG, longitude: longitude / DEG };
};

export const geographicToUtm = (latitude, longitude, crs) => {
    const c = ellipsoidFor(crs);
    if (!Number.isFinite(latitude) || latitude < -80 || latitude > 84 || !Number.isFinite(longitude) || longitude < -180 || longitude > 180)
        throw new Error('Geographic coordinates are outside the UTM area of use');
    if ((c.hemisphere === 'N' && latitude < 0) || (c.hemisphere === 'S' && latitude > 0))
        throw new Error('Latitude does not match the selected UTM hemisphere');
    const lat = latitude * DEG, lon = longitude * DEG;
    const lon0 = ((Number(c.zone) - 1) * 6 - 180 + 3) * DEG;
    const sinLat = Math.sin(lat), cosLat = Math.cos(lat), tanLat = Math.tan(lat);
    const n = c.a / Math.sqrt(1 - c.e2 * sinLat * sinLat);
    const t = tanLat * tanLat;
    const cc = c.ep2 * cosLat * cosLat;
    const aa = cosLat * (lon - lon0);
    const m = c.a * ((1 - c.e2 / 4 - 3 * c.e2 * c.e2 / 64 - 5 * c.e2 ** 3 / 256) * lat -
        (3 * c.e2 / 8 + 3 * c.e2 * c.e2 / 32 + 45 * c.e2 ** 3 / 1024) * Math.sin(2 * lat) +
        (15 * c.e2 * c.e2 / 256 + 45 * c.e2 ** 3 / 1024) * Math.sin(4 * lat) -
        (35 * c.e2 ** 3 / 3072) * Math.sin(6 * lat));
    const easting = 500000 + K0 * n * (aa + (1 - t + cc) * aa ** 3 / 6 +
        (5 - 18 * t + t * t + 72 * cc - 58 * c.ep2) * aa ** 5 / 120);
    let northing = K0 * (m + n * tanLat * (aa * aa / 2 + (5 - t + 9 * cc + 4 * cc * cc) * aa ** 4 / 24 +
        (61 - 58 * t + t * t + 600 * cc - 330 * c.ep2) * aa ** 6 / 720));
    if (c.hemisphere === 'S') northing += 10000000;
    return { easting, northing };
};

export const transformUtmCoordinate = (coordinate, source, target) => {
    const from = validateUtmCrs(source), to = validateUtmCrs(target);
    if (from.datum !== to.datum)
        throw new Error('Different datums need surveyed registration points; an unsupported datum shift will not be guessed');
    if (!Array.isArray(coordinate) || coordinate.length < 2 || !coordinate.slice(0, 3).every(Number.isFinite))
        throw new Error('Coordinate must contain finite easting, northing and optional height');
    const geographic = utmToGeographic(coordinate[0], coordinate[1], from);
    const projected = geographicToUtm(geographic.latitude, geographic.longitude, to);
    return [projected.easting, projected.northing, coordinate.length > 2 ? coordinate[2] : 0];
};

// Fit a 2D rigid transform (translation + rotation, with no scale) from
// surveyed design/map correspondences. Three points are required so RMS is
// a useful independent check instead of a perfect fit to the two parameters.
export const fitRigidRegistration = (designPoints, mapPoints) => {
    if (!Array.isArray(designPoints) || !Array.isArray(mapPoints) || designPoints.length < 3 || designPoints.length !== mapPoints.length)
        throw new Error('Provide at least three matching design and map points');
    if (designPoints.some(p => !Array.isArray(p) || p.length < 2 || !p.slice(0, 2).every(Number.isFinite)) ||
        mapPoints.some(p => !Array.isArray(p) || p.length < 2 || !p.slice(0, 2).every(Number.isFinite)))
        throw new Error('Registration points must have finite eastings and northings');

    const centroid = points => points.reduce((sum, p) => [sum[0] + p[0] / points.length, sum[1] + p[1] / points.length], [0, 0]);
    const sourceCentre = centroid(designPoints), targetCentre = centroid(mapPoints);
    let dot = 0, cross = 0, spread = 0;
    for (let i = 0; i < designPoints.length; i++) {
        const x = designPoints[i][0] - sourceCentre[0], y = designPoints[i][1] - sourceCentre[1];
        const u = mapPoints[i][0] - targetCentre[0], v = mapPoints[i][1] - targetCentre[1];
        dot += x * u + y * v;
        cross += x * v - y * u;
        spread += x * x + y * y;
    }
    if (spread < 1e-8 || Math.hypot(dot, cross) < 1e-8) throw new Error('Registration points do not span a usable area');
    const angle = Math.atan2(cross, dot);
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const tx = targetCentre[0] - cos * sourceCentre[0] + sin * sourceCentre[1];
    const ty = targetCentre[1] - sin * sourceCentre[0] - cos * sourceCentre[1];
    const hasHeight = designPoints.every(p => p.length >= 3 && Number.isFinite(p[2])) &&
        mapPoints.every(p => p.length >= 3 && Number.isFinite(p[2]));
    const verticalOffset = hasHeight
        ? mapPoints.reduce((sum, p, i) => sum + (p[2] - designPoints[i][2]) / designPoints.length, 0)
        : 0;
    const horizontalResiduals = designPoints.map((p, i) => {
        const x = cos * p[0] - sin * p[1] + tx - mapPoints[i][0];
        const y = sin * p[0] + cos * p[1] + ty - mapPoints[i][1];
        return Math.hypot(x, y);
    });
    const verticalResiduals = hasHeight
        ? designPoints.map((p, i) => p[2] + verticalOffset - mapPoints[i][2])
        : designPoints.map(() => 0);
    const horizontalRms = Math.sqrt(horizontalResiduals.reduce((sum, value) => sum + value * value, 0) / horizontalResiduals.length);
    const verticalRms = Math.sqrt(verticalResiduals.reduce((sum, value) => sum + value * value, 0) / verticalResiduals.length);
    const rms = Math.sqrt((horizontalResiduals.reduce((sum, value) => sum + value * value, 0) +
        verticalResiduals.reduce((sum, value) => sum + value * value, 0)) / designPoints.length);
    return { rotation: angle, cos, sin, translation: [tx, ty], verticalOffset, rms, horizontalRms, verticalRms,
        residuals: horizontalResiduals.map((value, i) => Math.hypot(value, verticalResiduals[i])) };
};

export const applyRigidRegistration = (coordinate, registration) => {
    if (!Array.isArray(coordinate) || coordinate.length < 2 || !coordinate.slice(0, 3).every(Number.isFinite))
        throw new Error('Coordinate must contain finite easting, northing and optional height');
    return [registration.cos * coordinate[0] - registration.sin * coordinate[1] + registration.translation[0],
        registration.sin * coordinate[0] + registration.cos * coordinate[1] + registration.translation[1],
        (coordinate.length > 2 ? coordinate[2] : 0) + (Number(registration.verticalOffset) || 0)];
};
